import classNames from "classnames";
import {
	forwardRef,
	PropsWithChildren,
	ReactElement,
	ReactNode,
	Ref,
	RefObject,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	AvailableMenuItem,
	AvailableMenuItemDraggableDataType,
	MenuItemKind,
	canHandleDraggableTypes
} from "./GlobalNavigationSidebar";
import {
	AppIcon,
	ButtonBase,
	DetailsBox,
	DropOver,
	FontIconName,
	Icon
} from "client/Components";
import styles from "./SideNavigation.module.less";
import {
	clamp,
	generateTrackingId,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	hasImplicitDependencyOn,
	flattenChildren,
	getReactNodeKey
} from "client/Misc/reactUtils";
import {
	DragOverEventArgs,
	DragOverEventResult,
	useDragAndDropEvents,
	useResizeObserver,
	useUserTimezone
} from "client/Hooks";
import { partition, range } from "client_legacy/ndaw/arrayHelpers";
import { useFrequentlyUsedTiles } from "../FrequentlyUsedTiles/useFrequentlyUsedTiles";
import { FrequentlyUsedTileModel } from "client/Api/Workspace/workspace";
import { getReactColorAttributes } from "client_legacy/ndaw/appColor";
import { localize } from "client_legacy/ndaw/culture";
import { FrequentlyUsedTilesList } from "../FrequentlyUsedTiles/FrequentlyUsedTilesList";
import { RenderImageType } from "client/Api/AppIcon/common";
import { TimeZone } from "client/DateAndTime/timezones";
import { useFrequentlyUsedTileNavigation } from "../FrequentlyUsedTiles/useFrequentlyUsedTileNavigation";

export type SideNavigationProps = {
	activeMenuItem: MenuItemKind | undefined;
	availableMenuItems: readonly AvailableMenuItem[];
	onActivateItem: (item: MenuItemKind) => void;
	onItemMouseEnter: (item: MenuItemKind) => void;
};

export const SideNavigation = (props: SideNavigationProps): ReactElement => {
	const {
		availableMenuItems,
		...listProps
	} = props;

	const [mainMenuItems, footerMenuItems] = useMemo(
		() => {
			return partition(
				availableMenuItems,
				x => x.position === "main"
			);
		},
		[availableMenuItems]
	);

	return (
		<div
			{...setTrackingId("side-navigation")}
			className={styles.root}
		>
			<div className={styles.mainContainer}>
				<MenuItemList
					{...listProps}
					items={mainMenuItems}
					trackingId="main-items"
				/>

				<FrequentlyUsedList
					trackingId="frequently-used-apps"
				/>
			</div>

			<hr className={styles.separator} />

			<MenuItemList
				{...listProps}
				items={footerMenuItems}
				trackingId="footer-items"
			/>
		</div>
	);
};

SideNavigation.displayName = "SideNavigation";

type ListProps = {
	trackingId: string;
	height?: "auto" | "stretch";
};

const List = forwardRef<HTMLUListElement, PropsWithChildren<ListProps>>(
	(props: PropsWithChildren<ListProps>, ref: Ref<HTMLUListElement>): ReactElement => {
		const {
			trackingId,
			height = "auto",
			children
		} = props;

		const childrenArray = useMemo(
			(): readonly ReactNode[] => {
				return flattenChildren(children);
			},
			[children]
		);

		return (
			<ul
				{...setTrackingId(trackingId)}
				className={classNames(
					styles.list,
					{ [styles.list_stretch]: height === "stretch" }
				)}
				ref={ref}
			>
				{childrenArray.map((x, i) => {
					return (
						<li key={getReactNodeKey(x, i)}>
							{x}
						</li>
					);
				})}
			</ul>
		);
	}
);
List.displayName = "List";

type ItemProps = {
	isActive?: boolean;
	onClick?: () => void;
	onMouseEnter?: () => void;
	allowedDraggableDataType?: AvailableMenuItemDraggableDataType;
	tooltip?: string;
	trackingId: string;
};

const Item = forwardRef<HTMLButtonElement, PropsWithChildren<ItemProps>>(
	(props: PropsWithChildren<ItemProps>, ref: Ref<HTMLButtonElement>): ReactElement => {
		const {
			isActive = false,
			onClick,
			onMouseEnter,
			trackingId,
			allowedDraggableDataType,
			tooltip,
			children
		} = props;

		const dragOverHandler = useCallback(
			(
				args: DragOverEventArgs,
				allowedDraggableDataType: AvailableMenuItemDraggableDataType
			): DragOverEventResult => {
				const { types } = args;
				if (!canHandleDraggableTypes(
					allowedDraggableDataType,
					isActive,
					types
				)) {
					return { isAccepted: false };
				}

				return {
					isAccepted: true,
					dropEffect: "move"
				};
			},
			[isActive]
		);

		const dragEnterHandler = useCallback(
			(
				args: DragOverEventArgs,
				allowedDraggableDataType: AvailableMenuItemDraggableDataType
			): void => {
				if (canHandleDraggableTypes(
					allowedDraggableDataType,
					isActive,
					args.types
				)) {
					onMouseEnter?.();
				}
			},
			[isActive, onMouseEnter]
		);

		const optionalDragOverHandler = useMemo(
			(): ((args: DragOverEventArgs) => DragOverEventResult) | undefined => {
				return isNotNil(onMouseEnter) && isNotNil(allowedDraggableDataType)
					? (args) => dragOverHandler(args, allowedDraggableDataType)
					: undefined;
			},
			[
				allowedDraggableDataType,
				dragOverHandler,
				onMouseEnter
			]
		);

		const optionalDragEnterHandler = useMemo(
			(): ((args: DragOverEventArgs) => void) | undefined => {
				return isNotNil(onMouseEnter) && isNotNil(allowedDraggableDataType)
					? (args) => dragEnterHandler(args, allowedDraggableDataType)
					: undefined;
			},
			[
				allowedDraggableDataType,
				dragEnterHandler,
				onMouseEnter
			]
		);

		const {
			isDropTarget: _,
			...dragAndDropEventHandlers
		} = useDragAndDropEvents({
			onDragOver: optionalDragOverHandler,
			onDragEnter: optionalDragEnterHandler
		});

		return (
			<ButtonBase
				{...dragAndDropEventHandlers}
				trackingId={trackingId}
				className={classNames(
					styles.item,
					{
						[styles.item_active]: isActive,
						[styles.item_clickable]: isNotNil(onClick)
					}
				)}
				onClick={onClick}
				onMouseEnter={onMouseEnter}
				aria-current={isActive}
				title={tooltip}
				ref={ref}
			>
				{children}
			</ButtonBase>
		);
	}
);

Item.displayName = "Item";

type MenuItemListProps = {
	activeMenuItem: MenuItemKind | undefined;
	items: readonly AvailableMenuItem[];
	onActivateItem: (item: MenuItemKind) => void;
	onItemMouseEnter: (item: MenuItemKind) => void;
	trackingId: string;
};

const MenuItemList = (props: MenuItemListProps): ReactElement => {
	const {
		activeMenuItem,
		onActivateItem,
		onItemMouseEnter,
		items,
		trackingId
	} = props;

	return (
		<List trackingId={trackingId}>
			{items.map((x) => {
				return (
					<Item
						key={x.kind}
						isActive={x.kind === activeMenuItem}
						onClick={() => onActivateItem(x.kind)}
						onMouseEnter={() => onItemMouseEnter(x.kind)}
						trackingId={x.trackingId}
						allowedDraggableDataType={x.allowedDraggableDataType}
						tooltip={x.name}
					>
						<Icon
							value={x.icon}
							size="large"
						/>
					</Item>
				);
			})}
		</List>
	);
};
MenuItemList.displayName = "MenuItemList";

type FrequentlyUsedTilesListProps = {
	trackingId: string;
};

const FrequentlyUsedList = (props: FrequentlyUsedTilesListProps): ReactElement | null => {
	const {
		trackingId
	} = props;

	const [items] = useFrequentlyUsedTiles();
	const userTimeZone: TimeZone = useUserTimezone();

	const listRef: RefObject<HTMLUListElement | null> = useRef<HTMLUListElement>(null);
	const { height: availableHeight } = useResizeObserver(listRef);
	const [amountToRender, setAmountToRender] = useState<number>(0);
	const [tileNavigationElement, invokeTileNavigationHandler] = useFrequentlyUsedTileNavigation(userTimeZone);
	const MAX_AMOUNT = 6;

	useLayoutEffect(
		(): void => {
			hasImplicitDependencyOn(items);

			const { current: listElement } = listRef;
			if (!listElement) {
				return;
			}

			// We assume all items have the same height for simplicity
			const itemHeight: number = listElement.querySelector("li")
				?.offsetHeight ?? 1;

			const amountOfItemsFittingInAvailableSpace: number = Math.max(
				0,
				Math.floor(availableHeight / itemHeight)
			);

			const shouldShowMoreItem: boolean = isNotNil(items) && (
				items.length > MAX_AMOUNT
					|| items.length > amountOfItemsFittingInAvailableSpace
			);

			setAmountToRender(
				clamp(
					amountOfItemsFittingInAvailableSpace - (shouldShowMoreItem ? 1 : 0),
					0,
					MAX_AMOUNT + (isNil(items) ? 1 : 0)
				)
			);
		},
		[items, availableHeight]
	);

	if (items?.length === 0) {
		return null;
	}

	const renderItems = (): ReactElement => {
		if (isNil(items)) {
			return (
				<>
					{range(amountToRender)
						.map((x): ReactElement => (
							<SkeletonItem
								key={x * -1}
								index={x}
							/>
						))}
				</>
			);
		}

		return (
			<>
				{items.slice(0, amountToRender)
					.map((x) => {
						return (
							<FrequentlyUsedItem
								key={x.tile.id}
								item={x}
								onInvoke={invokeTileNavigationHandler}
							/>
						);
					})}
				{amountToRender < items.length && (
					<MoreFrequentlyUsedItem
						key="more"
						items={items}
						onInvokeTileNavigation={invokeTileNavigationHandler}
					/>
				)}
			</>
		);
	};

	return (
		<>
			<hr className={styles.separator} />

			<List
				trackingId={trackingId}
				height="stretch"
				ref={listRef}
			>
				{renderItems()}
			</List>

			{tileNavigationElement}
		</>
	);
};
FrequentlyUsedList.displayName = "FrequentlyUsedList";

type FrequentlyUsedItemProps = {
	item: FrequentlyUsedTileModel;
	onInvoke: (item: FrequentlyUsedTileModel) => void;
};

const FrequentlyUsedItem = (props: FrequentlyUsedItemProps): ReactElement => {
	const {
		item,
		onInvoke
	} = props;

	const {
		tile,
		conditionalAccess,
		maintenanceWindow
	} = item;

	const { colorClass, colorStyle } = getReactColorAttributes(tile.color);

	const statusIcon: FontIconName | undefined = conditionalAccess
		? "ban"
		: maintenanceWindow
			? "screwdriver-wrench"
			: undefined;

	const clickHandler = useCallback(
		(): void => {
			onInvoke(item);
		},
		[item, onInvoke]
	);

	const { renderType } = tile.imageDisplayInfo;

	return (
		<Item
			onClick={clickHandler}
			trackingId={`${tile.id}`}
			tooltip={tile.name}
		>
			<div
				className={classNames(
					styles.appIcon,
					{
						[styles.appIcon_withStatus]: maintenanceWindow || conditionalAccess,
						[styles.appIcon_colorizedSvg]: renderType === RenderImageType.ColorizableSvg,
						[styles.appIcon_raster]: renderType === RenderImageType.Raster
					},
					colorClass
				)}
				style={colorStyle}
			>
				<AppIcon
					value={tile.imageDisplayInfo}
					color="inherit"
				/>
			</div>

			{statusIcon && (
				<div className={styles.statusIcon}>
					<Icon
						value={{
							kind: "font",
							name: statusIcon,
							color: "danger"
						}}
						size="extra-small"
					/>
				</div>
			)}
		</Item>
	);
};
FrequentlyUsedItem.displayName = "FrequentlyUsedItem";

type MoreFrequentlyUsedItemProps = {
	items: readonly FrequentlyUsedTileModel[];
	onInvokeTileNavigation: (item: FrequentlyUsedTileModel) => void;
};

const MoreFrequentlyUsedItem = (props: MoreFrequentlyUsedItemProps): ReactElement => {
	const {
		items,
		onInvokeTileNavigation
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const closeHandler = useCallback(
		(): void => setIsOpen(false),
		[]
	);

	const invokeTileNavigationHandler = useCallback(
		(item: FrequentlyUsedTileModel): void => {
			closeHandler();
			onInvokeTileNavigation(item);
		},
		[closeHandler, onInvokeTileNavigation]
	);

	return (
		<>
			<Item
				isActive={isOpen}
				onClick={() => setIsOpen((x) => !x)}
				trackingId="more"
				tooltip={localize("_More")}
				ref={ref}
			>
				<Icon
					value="ellipsis"
					size="large"
				/>
			</Item>

			{isOpen && ref.current && (
				<DropOver
					target={ref.current}
					targetAlignment="tl"
					contentAlignment="bl"
					onClose={closeHandler}
				>
					<DetailsBox
						variant="no-color"
						title={localize("_FrequentlyUsedApps")}
						onClose={closeHandler}
					>
						<DetailsBox.List>
							<FrequentlyUsedTilesList
								items={items}
								trackingId="list"
								onInvokeTileNavigation={invokeTileNavigationHandler}
							/>
						</DetailsBox.List>
					</DetailsBox>
				</DropOver>
			)}
		</>
	);
};
MoreFrequentlyUsedItem.displayName = "MoreFrequentlyUsedTilesItem";

type SkeletonItemProps = {
	index: number;
};

const SkeletonItem = (props: SkeletonItemProps): ReactElement => {
	const {
		index
	} = props;

	return (
		<Item
			trackingId={generateTrackingId("skeleton", `|${index}`)}
		>
			<div className={styles.skeleton}/>
		</Item>
	);
};
SkeletonItem.displayName = "SkeletonItem";
